import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

const ELEMENT_TAG = "pdf-v2-analyst-element";

/**
 * React wrapper around the `<pdf-v2-analyst-element>` custom element
 * that's shipped as a JS bundle from `app-mevi`. Public/index.html
 * loads the bundle (`/pdf-v2-element/main.js`); this component just
 * renders the element tag and bridges React's prop-passing model
 * (which doesn't auto-set non-string DOM properties on custom
 * elements in React 18) to the element's imperative surface.
 *
 * Props:
 *   • pdfBuffer — ArrayBuffer | Uint8Array of the PDF bytes.
 *   • formTemplate — parsed FormTemplate object (NOT a JSON string).
 *   • analystRole — `'analyst'` (default; full tuner) or `'annotator'`
 *       (limited tooling; Form Level keeps Data/Clear/Set Default H+W
 *       only, Field Level Style row hides). Form-studio passes
 *       `'annotator'` for annotator mode and `'analyst'` for the
 *       Mevi Preview tab (which is JSON-mode only and not reachable
 *       by annotators).
 *   • onSaveTemplateRequested — fires when the user clicks the
 *       Save Form Template button inside the element. Receives
 *       `{ template }` from the element's CustomEvent detail.
 *   • onFormDataChange — live form-data emit; passes the raw event
 *       payload through.
 *
 * Imperative methods (call via ref):
 *   • snapshotTemplate() → FormTemplate
 *   • snapshotFormData() → FormAnnotations
 *   • applyFormData(form) → { droppedContent }
 *
 * Note: DOM property assignment (`el.formTemplate = ...`) bypasses
 * React's attribute serialization, which is what custom elements
 * with object inputs need. The custom element's `@Input()` setter
 * picks up the assignment via Angular's element zone integration.
 */
const PdfV2AnalystElement = forwardRef(function PdfV2AnalystElement(
  {
    pdfBuffer,
    formTemplate,
    annotations,
    analystRole,
    onSaveTemplateRequested,
    onFormDataChange,
    onAnnotationsChange,
    onSignTabClick,
  },
  ref,
) {
  const elRef = useRef(null);
  // Custom element script is loaded `defer` from index.html, so on
  // first React render the tag may not yet be upgraded — assigning
  // DOM properties to a not-yet-upgraded custom element silently
  // fails (no setter exists). `customElements.whenDefined` resolves
  // when the bundle has called `customElements.define`. Once
  // upgraded, all subsequent prop changes flow through normally.
  const [upgraded, setUpgraded] = useState(
    () =>
      typeof window !== "undefined" &&
      typeof customElements !== "undefined" &&
      !!customElements.get(ELEMENT_TAG),
  );
  useEffect(() => {
    if (upgraded) return undefined;
    if (typeof customElements === "undefined") return undefined;
    let cancelled = false;
    customElements.whenDefined(ELEMENT_TAG).then(() => {
      if (!cancelled) setUpgraded(true);
    });
    return () => {
      cancelled = true;
    };
  }, [upgraded]);

  // Sync object props onto the custom element as DOM properties
  // (bypassing React's attribute serialization). Each prop gets its
  // own effect so updates are independent. Effects gated on
  // `upgraded` so the very first prop assignment lands on the
  // Angular-backed setter, not a plain HTMLElement.
  useEffect(() => {
    if (upgraded && elRef.current) elRef.current.pdfBuffer = pdfBuffer ?? null;
  }, [upgraded, pdfBuffer]);

  useEffect(() => {
    if (upgraded && elRef.current) elRef.current.formTemplate = formTemplate ?? null;
  }, [upgraded, formTemplate]);

  useEffect(() => {
    if (upgraded && elRef.current) elRef.current.annotations = annotations ?? null;
  }, [upgraded, annotations]);

  useEffect(() => {
    if (upgraded && elRef.current) elRef.current.analystRole = analystRole ?? "analyst";
  }, [upgraded, analystRole]);

  // Subscribe to CustomEvents the element fires. Cleanup on unmount.
  useEffect(() => {
    const el = elRef.current;
    if (!el) return undefined;
    const handlers = [];
    const sub = (type, fn) => {
      const h = (ev) => fn(ev.detail);
      el.addEventListener(type, h);
      handlers.push([type, h]);
    };
    if (onSaveTemplateRequested) sub("saveTemplateRequested", onSaveTemplateRequested);
    if (onFormDataChange) sub("formDataChange", onFormDataChange);
    if (onAnnotationsChange) sub("annotationsChange", onAnnotationsChange);
    if (onSignTabClick) sub("signTabClick", onSignTabClick);
    return () => {
      for (const [type, h] of handlers) el.removeEventListener(type, h);
    };
  }, [onSaveTemplateRequested, onFormDataChange, onAnnotationsChange, onSignTabClick]);

  // Expose imperative methods via the forwarded ref. Pass through to
  // the matching custom element method; the element exposes them
  // directly on the instance (Angular Elements wraps component
  // public methods as element methods).
  useImperativeHandle(
    ref,
    () => ({
      snapshotTemplate: () => elRef.current?.snapshotTemplate?.() ?? null,
      snapshotFormData: () => elRef.current?.snapshotFormData?.() ?? null,
      applyFormData: (form) => elRef.current?.applyFormData?.(form) ?? null,
    }),
    [],
  );

  // The element is loaded asynchronously via the `<script defer>` in
  // index.html. Until upgrade completes, the tag renders as an
  // unknown element (size 0). Style ensures it fills the parent so
  // it's correctly sized once Angular boots and the inner layout
  // mounts.
  return (
    <pdf-v2-analyst-element
      ref={elRef}
      style={{ display: "block", width: "100%", height: "100%" }}
    />
  );
});

export default PdfV2AnalystElement;
